<script setup>
import { onMounted, reactive, ref } from 'vue'
import http from '@/api/http'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Select from 'primevue/select'
import ToggleSwitch from 'primevue/toggleswitch'
import DatePicker from 'primevue/datepicker'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import InputText from 'primevue/inputtext'

const rows = ref([])
const total = ref(0)
const loading = ref(false)
const error = ref('')

const filters = reactive({
  source: null,
  found_only: false,
  has_external_id: false,
  date_from: null,
  date_to: null,
  external_id: '',
  request_q: '',
  page: 1,
  perPage: 20,
})

const sourceOptions = [
  { label: 'Все источники', value: null },
  { label: 'Админка', value: 'admin' },
  { label: 'API (Bearer)', value: 'api' },
]

const showDetail = ref(false)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')

function fmtDateParam(d) {
  if (!d) return null
  if (typeof d === 'string') return d
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = {
      page: filters.page,
      per_page: filters.perPage,
    }
    if (filters.source) params.source = filters.source
    if (filters.found_only) params.found_only = 1
    if (filters.has_external_id) params.has_external_id = 1
    const df = fmtDateParam(filters.date_from)
    if (df) params.date_from = `${df} 00:00:00`
    const dt = fmtDateParam(filters.date_to)
    if (dt) params.date_to = `${dt} 23:59:59`
    if (filters.external_id.trim()) params.external_id = filters.external_id.trim()
    if (filters.request_q.trim()) params.request_q = filters.request_q.trim()

    const { data } = await http.get('/check-log', { params })
    rows.value = data?.items ?? []
    total.value = data?.pagination?.total ?? rows.value.length
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Не удалось загрузить журнал'
  } finally {
    loading.value = false
  }
}

function onPage(e) {
  filters.page = e.page + 1
  filters.perPage = e.rows
  load()
}

function applyFilters() {
  filters.page = 1
  load()
}

function fmtDate(s) {
  if (!s) return '—'
  try {
    return new Date(s.replace(' ', 'T')).toLocaleString('ru-RU')
  } catch (_) {
    return s
  }
}

async function openDetail(row) {
  showDetail.value = true
  detail.value = null
  detailError.value = ''
  detailLoading.value = true
  try {
    const { data } = await http.get(`/check-log/${row.id}`)
    detail.value = data
  } catch (e) {
    detailError.value = e?.response?.data?.error?.message || 'Не удалось загрузить деталь'
  } finally {
    detailLoading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page">
    <div class="toolbar">
      <Select
        v-model="filters.source"
        :options="sourceOptions"
        option-label="label"
        option-value="value"
        placeholder="Источник"
        @change="applyFilters"
      />
      <DatePicker
        v-model="filters.date_from"
        date-format="dd.mm.yy"
        placeholder="С даты"
        show-icon
        @date-select="applyFilters"
      />
      <DatePicker
        v-model="filters.date_to"
        date-format="dd.mm.yy"
        placeholder="По дату"
        show-icon
        @date-select="applyFilters"
      />
      <InputText
        v-model="filters.external_id"
        placeholder="Внутренний ИД"
        class="toolbar__external"
        @keyup.enter="applyFilters"
      />
      <InputText
        v-model="filters.request_q"
        placeholder="Поиск в запросе"
        class="toolbar__request-q"
        @keyup.enter="applyFilters"
      />
      <div class="toolbar__switch">
        <ToggleSwitch
          v-model="filters.found_only"
          input-id="foundOnly"
          @change="applyFilters"
        />
        <label for="foundOnly">Только с совпадениями</label>
      </div>
      <div class="toolbar__switch">
        <ToggleSwitch
          v-model="filters.has_external_id"
          input-id="hasExternalId"
          @change="applyFilters"
        />
        <label for="hasExternalId">С внутр. ИД</label>
      </div>

      <div class="toolbar__spacer" />

      <Button
        icon="pi pi-refresh"
        label="Обновить"
        severity="secondary"
        outlined
        :loading="loading"
        @click="load"
      />
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <DataTable
      :value="rows"
      :loading="loading"
      lazy
      paginator
      :rows="filters.perPage"
      :total-records="total"
      :rows-per-page-options="[10, 20, 50, 100]"
      data-key="id"
      striped-rows
      @page="onPage"
      @row-click="(e) => openDetail(e.data)"
      class="clickable"
    >
      <Column field="id" header="#" style="width: 5rem" />
      <Column header="Когда" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.date_create) }}</template>
      </Column>
      <Column header="Источник" style="width: 9rem">
        <template #body="{ data }">
          <Tag
            v-if="data.source === 'admin'"
            value="админка"
            severity="info"
          />
          <Tag v-else value="API" severity="secondary" />
        </template>
      </Column>
      <Column header="Токен" style="min-width: 14rem">
        <template #body="{ data }">
          <template v-if="data.token_id">
            <div>{{ data.token_description || '—' }}</div>
            <div class="muted"><code>{{ data.token_key }}</code></div>
          </template>
          <span v-else class="muted">—</span>
        </template>
      </Column>
      <Column header="Внутр. ИД" style="min-width: 9rem">
        <template #body="{ data }">
          <code v-if="data.external_id">{{ data.external_id }}</code>
          <span v-else class="muted">—</span>
        </template>
      </Column>
      <Column field="scope" header="Scope" style="width: 7rem" />
      <Column field="subjects_count" header="Субъектов" style="width: 7rem" />
      <Column header="Найдено" style="width: 8rem">
        <template #body="{ data }">
          <Tag
            v-if="data.found_count > 0"
            :value="String(data.found_count)"
            severity="danger"
          />
          <Tag v-else value="0" severity="success" />
        </template>
      </Column>
      <Column header="" style="width: 4rem">
        <template #body="{ data }">
          <Button
            text
            rounded
            icon="pi pi-search"
            aria-label="Подробнее"
            @click.stop="openDetail(data)"
          />
        </template>
      </Column>
      <template #empty>
        <p class="muted">Записей нет</p>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showDetail"
      modal
      :style="{ width: '60rem', maxWidth: '95vw' }"
      header="Деталь проверки"
    >
      <div v-if="detailLoading" class="muted">Загрузка…</div>
      <Message v-else-if="detailError" severity="error" :closable="false">{{ detailError }}</Message>
      <template v-else-if="detail">
        <div class="detail__head">
          <div><strong>#{{ detail.id }}</strong> · {{ fmtDate(detail.date_create) }}</div>
          <div class="muted">
            Источник: {{ detail.source === 'admin' ? 'админка' : 'API' }}
            <span v-if="detail.token_key">· токен: <code>{{ detail.token_key }}</code></span>
            <span v-if="detail.external_id">· внутр. ИД: <code>{{ detail.external_id }}</code></span>
          </div>
          <div>
            <Tag
              :severity="detail.found_count > 0 ? 'danger' : 'success'"
              :value="`найдено: ${detail.found_count}`"
            />
          </div>
        </div>

        <div class="detail__cols">
          <div>
            <h4>Запрос</h4>
            <pre>{{ JSON.stringify(detail.request, null, 2) }}</pre>
          </div>
          <div>
            <h4>Ответ</h4>
            <pre>{{ JSON.stringify(detail.response, null, 2) }}</pre>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.toolbar__switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.toolbar__spacer {
  flex: 1;
}
.toolbar__external {
  min-width: 12rem;
}
.toolbar__request-q {
  min-width: 14rem;
}
.error { color: #b91c1c; margin: 0 0 0.75rem; }

.clickable :deep(tbody tr) { cursor: pointer; }

.detail__head {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
}

.detail__cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 800px) {
  .detail__cols { grid-template-columns: 1fr; }
}

.detail__cols h4 {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
}

.detail__cols pre {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.78rem;
  max-height: 65vh;
  overflow: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
